/**
 * BehaviorTracker — V2 localStorage scoring + negative signals.
 *
 * Two storage stores:
 *   wd_behavior_v1         — event log (view/add/purchase) for scoreFor()
 *   wd_behavior_counters_v1 — per-product counters for negative signals:
 *     { impressions, clicks, removes, hideUntilRemove, hideUntilIgnore }
 *
 * Positive scoring (max +14 per product):
 *   - View interest: capped +5
 *       weightedViews ≥ 1 → +1, ≥ 2 → +3, ≥ 3 → +5
 *   - Added to cart before: +6 × strongest event's decay weight
 *   - Past purchase taste boost: +3 (no decay — taste boost doesn't expire)
 *
 * Time decay (per event for view/atc only):
 *   < 1h → 1.00, < 24h → 0.80, 2–7d → 0.50, > 7d → 0.00
 *
 * Negative signals (V2):
 *   - Removed once   → −3 score penalty
 *   - Removed twice  → hide 24h
 *   - Removed 3+×    → hide 7d
 *   - Re-engagement (view / ATC) → clear all penalties
 *   - Shown 3× no click → −2 score penalty
 *   - Shown 5× no click → hide 24h
 *   - Click → reset ignore-hide, increment clicks (reduces net ignored)
 *   - ATC → clear both removal AND ignored counters entirely
 *
 * Recent-purchases 90-day hard exclusion merges localStorage purchase events
 * with server-rendered window.__customerOrders.
 *
 * Public API (window.BehaviorTracker):
 *   recordView(productId)
 *   recordAddToCart(productId)
 *   recordPurchase(productId)
 *   recordImpression(productId)   // card was actually seen
 *   recordClick(productId)        // card was clicked through
 *   scoreFor(productId) → 0..14   (positive only)
 *   removalPenalty(productId)     → -3 or 0
 *   impressionPenalty(productId)  → -2 or 0
 *   isHidden(productId)           → bool (in 24h/7d suppression)
 *   recentPurchases(daysBack=90)  → Set<productId>
 *   all() → array of events (debug)
 *   counters() → counters object (debug)
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'wd_behavior_v1';
  const COUNTERS_KEY = 'wd_behavior_counters_v2';
  const MAX_EVENTS = 200;

  const HOUR_MS = 60 * 60 * 1000;
  const DAY_MS = 24 * HOUR_MS;
  const SCORE_WINDOW_MS = 7 * DAY_MS;

  // ---------- Event log ----------

  function load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) { return []; }
  }

  function save(events) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_EVENTS))); }
    catch (e) {}
  }

  function record(type, productId) {
    const id = Number(productId);
    if (!id) return;
    const events = load();
    events.push({ id, type, ts: Date.now() });
    save(events);
  }

  // ---------- Counter store ----------

  function loadCounters() {
    try {
      const raw = localStorage.getItem(COUNTERS_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (e) { return {}; }
  }

  function saveCounters(c) {
    try { localStorage.setItem(COUNTERS_KEY, JSON.stringify(c)); } catch (e) {}
  }

  function defaultCounter() {
    return { impressions: 0, clicks: 0, removes: 0, hideUntilRemove: 0, hideUntilIgnore: 0 };
  }

  function getCounter(id) {
    const c = loadCounters();
    return c[id] || defaultCounter();
  }

  function patchCounter(id, patch) {
    const all = loadCounters();
    all[id] = Object.assign(defaultCounter(), all[id] || {}, patch);
    saveCounters(all);
    return all[id];
  }

  // ---------- Re-engagement helpers ----------

  function clearRemovalPenalty(id) {
    patchCounter(id, { removes: 0, hideUntilRemove: 0 });
  }
  function clearIgnoredPenalty(id) {
    const cur = getCounter(id);
    patchCounter(id, { impressions: cur.clicks, hideUntilIgnore: 0 });
  }
  function clearAllNegative(id) {
    const cur = getCounter(id);
    patchCounter(id, {
      removes: 0, hideUntilRemove: 0,
      impressions: cur.clicks, hideUntilIgnore: 0,
    });
  }

  // ---------- Public recorders ----------

  function recordView(productId) {
    const id = Number(productId);
    if (!id) return;
    record('view', id);
    // V2 + QA: a product page view clears the REMOVAL penalty (renewed
    // interest after a remove), but does NOT clear the ignored count —
    // ignored only resets on a recommendation click or ATC. Otherwise a
    // customer who casually browses a hidden product would un-suppress it.
    clearRemovalPenalty(id);
  }

  function recordAddToCart(productId) {
    const id = Number(productId);
    if (!id) return;
    record('add', id);
    // ATC is the strongest re-engagement: clear remove + ignored counters.
    clearAllNegative(id);
  }

  function recordPurchase(productId) { record('purchase', productId); }

  function recordImpression(productId) {
    const id = Number(productId);
    if (!id) return;
    const cur = getCounter(id);
    const impressions = cur.impressions + 1;
    let hideUntilIgnore = cur.hideUntilIgnore;
    // 5+ net ignored → hide 24h (only set if not already hidden).
    const net = impressions - cur.clicks;
    if (net >= 5 && hideUntilIgnore < Date.now()) {
      hideUntilIgnore = Date.now() + DAY_MS;
    }
    patchCounter(id, { impressions, hideUntilIgnore });
  }

  function recordClick(productId) {
    const id = Number(productId);
    if (!id) return;
    const cur = getCounter(id);
    patchCounter(id, {
      clicks: cur.clicks + 1,
      hideUntilIgnore: 0,
    });
  }

  function recordRemove(productId) {
    const id = Number(productId);
    if (!id) return;
    const cur = getCounter(id);
    const removes = cur.removes + 1;
    let hideUntilRemove = cur.hideUntilRemove;
    if (removes === 2) hideUntilRemove = Date.now() + DAY_MS;
    else if (removes >= 3) hideUntilRemove = Date.now() + 7 * DAY_MS;
    patchCounter(id, { removes, hideUntilRemove });
  }

  // ---------- Scoring ----------

  function decayWeight(ageMs) {
    if (ageMs < HOUR_MS) return 1.0;
    if (ageMs < DAY_MS) return 0.8;
    if (ageMs < SCORE_WINDOW_MS) return 0.5;
    return 0;
  }

  function scoreFor(productId) {
    const id = Number(productId);
    if (!id) return 0;
    const now = Date.now();
    const events = load().filter((e) => e.id === id && (now - e.ts) < SCORE_WINDOW_MS);
    if (!events.length) {
      // No decayable events, but purchase counts even outside the 7-day window
      // (the +3 taste boost has no decay per V2).
      const everPurchased = load().some((e) => e.id === id && e.type === 'purchase');
      return everPurchased ? 3 : 0;
    }

    const weightedViews = events
      .filter((e) => e.type === 'view')
      .reduce((sum, e) => sum + decayWeight(now - e.ts), 0);
    let viewScore = 0;
    if (weightedViews >= 3) viewScore = 5;
    else if (weightedViews >= 2) viewScore = 3;
    else if (weightedViews >= 1) viewScore = 1;

    const adds = events.filter((e) => e.type === 'add');
    const atcScore = adds.length
      ? 6 * Math.max.apply(null, adds.map((e) => decayWeight(now - e.ts)))
      : 0;

    // Past purchase taste boost — flat +3, NO decay (per V2 simulator).
    const everPurchased = load().some((e) => e.id === id && e.type === 'purchase');
    const purchaseScore = everPurchased ? 3 : 0;

    return Math.min(14, viewScore + atcScore + purchaseScore);
  }

  function removalPenalty(productId) {
    const cur = getCounter(productId);
    // Only the "single hesitation" penalty applies as a score deduction.
    // 2+ removes flip to hide via isHidden() — no double-penalty.
    if (cur.removes === 1 && cur.hideUntilRemove < Date.now()) return -3;
    return 0;
  }

  function impressionPenalty(productId) {
    const cur = getCounter(productId);
    const net = cur.impressions - cur.clicks;
    if (cur.hideUntilIgnore >= Date.now()) return 0; // hidden anyway
    if (net >= 3) return -2;
    return 0;
  }

  function isHidden(productId) {
    const cur = getCounter(productId);
    const now = Date.now();
    return cur.hideUntilRemove > now || cur.hideUntilIgnore > now;
  }

  function recentPurchases(daysBack) {
    const days = typeof daysBack === 'number' ? daysBack : 90;
    const cutoff = Date.now() - days * DAY_MS;
    const ids = new Set();
    for (const e of load()) {
      if (e.type === 'purchase' && e.ts >= cutoff) ids.add(e.id);
    }
    if (Array.isArray(window.__customerOrders)) {
      for (const o of window.__customerOrders) {
        if (o && o.id && o.ts * 1000 >= cutoff) ids.add(Number(o.id));
      }
    }
    return ids;
  }

  // ---------- Cart-diff: adds + removes ----------

  function diffCartAdds(prev, next) {
    const prevQty = new Map();
    if (prev && Array.isArray(prev.items)) {
      for (const it of prev.items) {
        prevQty.set(it.product_id, (prevQty.get(it.product_id) || 0) + it.quantity);
      }
    }
    const added = [];
    if (next && Array.isArray(next.items)) {
      for (const it of next.items) {
        const before = prevQty.get(it.product_id) || 0;
        if (it.quantity > before) added.push(it.product_id);
      }
    }
    return added;
  }

  function diffCartRemoves(prev, next) {
    const nextQty = new Map();
    if (next && Array.isArray(next.items)) {
      for (const it of next.items) {
        nextQty.set(it.product_id, (nextQty.get(it.product_id) || 0) + it.quantity);
      }
    }
    const fully = [];
    if (prev && Array.isArray(prev.items)) {
      const seen = new Set();
      for (const it of prev.items) {
        if (seen.has(it.product_id)) continue;
        seen.add(it.product_id);
        const after = nextQty.get(it.product_id) || 0;
        // Spec: "removed from cart" → only count fully-removed lines, not qty-down.
        if (after === 0) fully.push(it.product_id);
      }
    }
    return fully;
  }

  let lastCartSnapshot = null;

  function fetchCartSnapshot() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then((r) => r.json())
      .catch(() => null);
  }

  function init() {
    const productId = window.ShopifyAnalytics
      && window.ShopifyAnalytics.meta
      && window.ShopifyAnalytics.meta.product
      && window.ShopifyAnalytics.meta.product.id;
    if (productId) recordView(productId);

    fetchCartSnapshot().then((snap) => { lastCartSnapshot = snap; });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const next = event && event.cartData ? event.cartData : null;
        if (!next) return;
        const added = diffCartAdds(lastCartSnapshot, next);
        for (const id of added) recordAddToCart(id);
        const removed = diffCartRemoves(lastCartSnapshot, next);
        for (const id of removed) recordRemove(id);
        lastCartSnapshot = next;
      });
    }
  }

  window.BehaviorTracker = {
    recordView,
    recordAddToCart,
    recordPurchase,
    recordImpression,
    recordClick,
    scoreFor,
    removalPenalty,
    impressionPenalty,
    isHidden,
    recentPurchases,
    all: load,
    counters: loadCounters,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
